import { useMemo } from 'react';

import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import c from './_profileSocials.module.scss';

export interface ProfileSocialsProps {
  tripadvisor?: string | null;
  yelp?: string | null;
  youtube?: string | null;
}

interface SocialLink {
  label: string;
  url: string;
  icon?: IconProp;
}

function toHttpsUrl(value: string): string {
  return /^(?:f|ht)tps?:\/\//i.test(value) ? value : `https://${value}`;
}

export function ProfileSocials({ tripadvisor, yelp, youtube }: ProfileSocialsProps) {
  const socials = useMemo((): SocialLink[] => {
    const result: SocialLink[] = [];
    if (tripadvisor) {
      result.push({
        label: i18n.translate('directoryProfileView.socialTripadvisor') as string,
        url: toHttpsUrl(tripadvisor),
      });
    }
    if (yelp) {
      result.push({
        label: i18n.translate('directoryProfileView.socialYelp') as string,
        url: toHttpsUrl(yelp),
        icon: ['fab', 'yelp'],
      });
    }
    if (youtube) {
      result.push({
        label: i18n.translate('directoryProfileView.socialYoutube') as string,
        url: toHttpsUrl(youtube),
        icon: ['fab', 'youtube'],
      });
    }
    return result;
  }, [tripadvisor, yelp, youtube]);

  if (socials.length === 0) return null;

  return (
    <div className={clsx('profile-socials', c.wrapper)}>
      <div className={clsx('profile-socials__title', c.title)}>
        {i18n.translate('directoryProfileView.socialsTitle')}
      </div>
      {socials.map(({ label, url, icon }) => {
        return (
          <a
            key={label}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx('profile-socials__social', c.social)}
          >
            {icon && <FontAwesomeIcon icon={icon} className={c.icon} />}
            {label}
          </a>
        );
      })}
    </div>
  );
}
